import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt, mntType } from "theme/maintenanceTokens";
import { useToast } from "components/toast";

import { emailQuote } from "api/Quotes";
import { getClientContacts } from "api/ServicePlans";
import CorrigoExportPanel from "containers/Quotes/components/CorrigoExportPanel";

/**
 * Session 65 item 3 (root cause C2) — the ONE quote-delivery surface.
 *
 * WHY THIS EXISTS. "Send quote" had two shapes that had drifted: the
 * WO-level `SendQuoteModal` offered a segmented Email | CorrigoPro
 * toggle, while the Quote tab embedded a bare `CorrigoExportPanel` with
 * NO email option. Same decision (how to get this quote in front of the
 * client), two different sets of choices depending on where you clicked.
 * This panel is that toggle + both branches, extracted so the modal and
 * the embedded Quote-tab slot render the SAME thing — one implementation,
 * one set of options, no drift (RC2).
 *
 *   EMAIL branch (default when plan is quote_fed or the WO has no plan):
 *     a recipient PICKER over the client's contacts on file plus the site
 *     contact, a freeform fallback field, optional CC, then Send ->
 *     emailQuote.
 *
 *   CORRIGO branch (default when plan is work_order_fed):
 *     the canonical `CorrigoExportPanel` (mockup 08) mounted in place
 *     (RC5) — paste export, Mark posted, Accept on behalf. Not
 *     reimplemented here.
 *
 * WO progress is DERIVED (WO_STATUS_DERIVATION.md); this surface never
 * hand-marks a "sent" state.
 *
 * Props mirror the old SendQuoteModal branch props. `initialBranch`
 * lets a caller open straight to Corrigo (e.g. the WhatsNext
 * "Accept on behalf" step, which is only ever a Corrigo-plan step).
 */
export default function QuoteDeliveryPanel({
  quoteId,
  quote,
  intakeStyle,
  clientId,
  defaultEmail,
  defaultCc,
  initialBranch,
  onSent,
  onPosted,
  onAccepted,
  onClose,
}: {
  quoteId: string;
  quote?: any;
  intakeStyle?: "work_order_fed" | "quote_fed";
  clientId?: string | null;
  defaultEmail?: string | null;
  defaultCc?: string[] | null;
  initialBranch?: "email" | "corrigo";
  onSent?: () => void;
  onPosted?: () => void;
  onAccepted?: () => void;
  onClose?: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();

  // Default branch: an explicit initialBranch wins (a caller that knows
  // the step is a Corrigo one), else the plan's intake style. The
  // operator can always switch.
  const [branch, setBranch] = useState<"email" | "corrigo">(
    initialBranch ?? (intakeStyle === "work_order_fed" ? "corrigo" : "email"),
  );

  // Email branch state
  const [recipient, setRecipient] = useState<string>(defaultEmail || "");
  const [ccText, setCcText] = useState<string>(
    Array.isArray(defaultCc) ? defaultCc.join(", ") : "",
  );
  const [sending, setSending] = useState(false);

  // Recipient picker — the client's contacts on file. Fetched once when
  // the email branch is in view and a client is known. null = not loaded.
  const [contacts, setContacts] = useState<any[] | null>(null);
  useEffect(() => {
    if (branch !== "email" || !clientId || contacts !== null) return;
    let alive = true;
    getClientContacts({ data: { clientId } }).then((rows: any[]) => {
      if (alive) setContacts(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, [branch, clientId, contacts]);

  // The pickable recipient options: the site contact first (it is the
  // person the work order named), then the client's contacts on file,
  // de-duplicated by email. Contacts with no email cannot be a recipient
  // and are dropped from the list.
  const recipientOptions = useMemo(() => {
    const out: Array<{ email: string; name: string; kind: "site" | "contact" }> = [];
    const seen = new Set<string>();
    const add = (email: string | null | undefined, name: string, kind: "site" | "contact") => {
      const e = (email || "").trim();
      if (!e) return;
      const key = e.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ email: e, name: name || e, kind });
    };
    add(defaultEmail, "Site contact", "site");
    for (const c of contacts || []) {
      const name = [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim();
      add(c?.email, name || c?.email, "contact");
    }
    return out;
  }, [defaultEmail, contacts]);

  const emailValid = useMemo(() => {
    const v = recipient.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [recipient]);

  const addCc = (email: string) => {
    const existing = ccText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (existing.some((e) => e.toLowerCase() === email.toLowerCase())) return;
    setCcText([...existing, email].join(", "));
  };

  const sendEmail = async () => {
    if (!emailValid) {
      toast.add("Enter a valid recipient email address.", "var(--theme-ui-colors-danger)");
      return;
    }
    setSending(true);
    const cc = ccText
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const res = await emailQuote({
      data: { id: quoteId, recipientEmail: recipient.trim(), cc },
    });
    setSending(false);
    if (res?.success) {
      const suppressed = res?.message?.includes("suppressed");
      toast.add(
        suppressed ? "This plan is set not to email quotes. Nothing was sent." : "Quote emailed.",
        suppressed ? "var(--theme-ui-colors-warning)" : "var(--theme-ui-colors-success)",
      );
      if (onSent) onSent();
      if (onClose) onClose();
    } else {
      toast.add(res?.message || "Could not send the quote email.", "var(--theme-ui-colors-danger)");
    }
  };

  const goToCorrigoPanel = () => {
    if (onClose) onClose();
    navigate(`/quotes/${quoteId}`);
  };

  const isEmail = branch === "email";

  return (
    <Box>
      {/* Branch toggle — a segmented control, not two loose buttons. */}
      <Flex
        sx={{
          background: mnt.bgSubtle,
          border: `0.5px solid ${mnt.divider}`,
          borderRadius: "8px",
          padding: "3px",
          gap: "3px",
          marginBottom: "18px",
          width: "fit-content",
        }}
      >
        <SegTab active={isEmail} label="Email" onClick={() => setBranch("email")} />
        <SegTab active={!isEmail} label="CorrigoPro" onClick={() => setBranch("corrigo")} />
      </Flex>

      {isEmail ? (
        <Box>
          <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
            Email this quote to the recipient below. They get a link to review and accept it in the
            portal.
          </Subtext>

          {/* Recipient picker — who to send to, on file. */}
          <Box sx={{ marginTop: "16px" }}>
            <Box
              sx={{
                ...mntType.labelSm,
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              Choose a recipient
            </Box>

            {contacts === null && clientId ? (
              <Box sx={{ ...mntType.body, color: mnt.textFaint, padding: "6px 2px" }}>
                Loading contacts on file...
              </Box>
            ) : recipientOptions.length === 0 ? (
              <Box
                sx={{
                  background: mnt.bgBeige,
                  borderRadius: "6px",
                  padding: "10px 12px",
                }}
              >
                <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                  No contacts on file for this client yet. Enter an address below.
                </Span>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `0.5px solid ${mnt.divider}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {recipientOptions.map((opt, i) => {
                  const selected = recipient.trim().toLowerCase() === opt.email.toLowerCase();
                  return (
                    <Flex
                      key={opt.email}
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "9px 12px",
                        borderTop: i === 0 ? "none" : `0.5px solid ${mnt.divider}`,
                        background: selected ? mnt.bgBeige : mnt.cardBg,
                        cursor: "pointer",
                        "&:hover": { background: selected ? mnt.bgBeige : mnt.bgSubtle },
                      }}
                      onClick={() => setRecipient(opt.email)}
                    >
                      <Flex sx={{ alignItems: "center", gap: "10px", minWidth: 0 }}>
                        <Box
                          sx={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            border: `2px solid ${selected ? mnt.ink : mnt.textFaint}`,
                            background: selected ? mnt.ink : "transparent",
                            flex: "0 0 auto",
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Flex sx={{ alignItems: "center", gap: "8px" }}>
                            <Box sx={{ ...mntType.body, fontWeight: 500 }}>{opt.name}</Box>
                            {opt.kind === "site" && (
                              <Box sx={{ ...mntType.labelSm, color: mnt.teal }}>Site contact</Box>
                            )}
                          </Flex>
                          <Box
                            sx={{
                              ...mntType.labelSm,
                              color: mnt.textMuted,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {opt.email}
                          </Box>
                        </Box>
                      </Flex>
                      <button
                        type="button"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          addCc(opt.email);
                        }}
                        style={{
                          flex: "0 0 auto",
                          background: "transparent",
                          border: "none",
                          padding: "2px 4px",
                          fontFamily: "Poppins",
                          fontSize: "11px",
                          color: mnt.link,
                          cursor: "pointer",
                        }}
                      >
                        + Cc
                      </button>
                    </Flex>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ marginTop: "14px" }}>
            <TextInput
              name="recipient"
              label={recipientOptions.length ? "Or enter an email address" : "Recipient email"}
              value={recipient}
              onChange={(v: any) => setRecipient(v ?? "")}
              placeholder="name@company.com"
              $errors={recipient.length > 0 && !emailValid ? "Enter a valid email address." : null}
            />
          </Box>
          <Box sx={{ marginTop: "12px" }}>
            <TextInput
              name="cc"
              label="CC (optional, comma-separated)"
              value={ccText}
              onChange={(v: any) => setCcText(v ?? "")}
              placeholder="another@company.com, third@company.com"
            />
          </Box>
          <Flex sx={{ marginTop: "18px", justifyContent: "flex-end" }}>
            <BasicButton
              type="button"
              scheme="primary"
              $submitting={sending}
              keepText={true}
              disabled={!emailValid}
              onClick={sendEmail}
            >
              Send email
            </BasicButton>
          </Flex>
        </Box>
      ) : (
        <Box>
          <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
            This plan is managed through CorrigoPro. The quote is posted to Corrigo, not emailed to
            the client.
          </Subtext>
          {quote?.id ? (
            <Box sx={{ marginTop: "10px" }}>
              <CorrigoExportPanel
                quote={quote}
                onPosted={onPosted}
                onAccepted={() => {
                  // Accept-on-behalf is a distinct outcome from a plain
                  // send: it turns the quote into scheduled work, so the
                  // WO should jump to the project tab. Prefer the dedicated
                  // onAccepted; fall back to onSent for parents that don't
                  // distinguish.
                  if (onAccepted) onAccepted();
                  else if (onSent) onSent();
                  if (onClose) onClose();
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                marginTop: "14px",
                padding: "12px 14px",
                background: mnt.bgBeige,
                borderRadius: "8px",
              }}
            >
              <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.ink}>
                Still loading the quote. If this persists, open the quote to post it.
              </Span>
              <Flex sx={{ marginTop: "14px", justifyContent: "flex-end" }}>
                <BasicButton type="button" scheme="primary" keepText={true} onClick={goToCorrigoPanel}>
                  Open quote &amp; Corrigo export
                </BasicButton>
              </Flex>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function SegTab({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? mnt.cardBg : "transparent",
        border: active ? `0.5px solid ${mnt.divider}` : "0.5px solid transparent",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        borderRadius: "6px",
        padding: "6px 16px",
        fontSize: "12px",
        fontFamily: "Poppins",
        fontWeight: 600,
        color: active ? mnt.ink : mnt.textMuted,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
